import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", category: "", description: "", duration: 30, fee: "", is_active: true };

export default function Services() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/services"); setServices(data.services); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, category: s.category || "", description: s.description || "", duration: s.duration, fee: s.fee ?? "", is_active: s.is_active }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Service name is required"); return; }
    setSaving(true);
    const payload = { ...form, duration: parseInt(form.duration, 10), fee: form.fee === "" ? null : parseFloat(form.fee) };
    try {
      if (editing) await api.put(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="services-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate("/doctors")}><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Services</h1>
            <p className="mt-1 text-sm text-slate-500">Healthcare services offered by your organization</p>
          </div>
        </div>
        <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-service-button"><Plus className="h-4 w-4" /> Add Service</Button>
      </div>

      <Card className="overflow-hidden shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200 hover:bg-transparent">
              <TableHead className="pl-6 text-xs uppercase tracking-wider text-slate-400">Service</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Category</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Duration</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Fee</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Status</TableHead>
              <TableHead className="pr-6 text-right text-xs uppercase tracking-wider text-slate-400">Edit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? [...Array(5)].map((_, i) => <TableRow key={i}><TableCell colSpan={6} className="px-6"><Skeleton className="h-8 w-full" /></TableCell></TableRow>)
              : services.map((s) => (
                <TableRow key={s.id} className="border-slate-100" data-testid="service-row">
                  <TableCell className="pl-6 font-medium text-slate-900">{s.name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{s.category || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-600">{s.duration}m</TableCell>
                  <TableCell className="font-mono text-xs text-slate-600">{s.fee ? `₹${s.fee}` : "—"}</TableCell>
                  <TableCell><Badge className={s.is_active ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" : "bg-slate-100 text-slate-500"}>{s.is_active ? "Active" : "Inactive"}</Badge></TableCell>
                  <TableCell className="pr-6 text-right"><Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}><Pencil className="h-4 w-4" /></Button></TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="service-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Service" : "Add Service"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-name" /></div>
            <div className="space-y-1.5"><Label>Category / Department</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Duration (min)</Label><Input type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} data-testid="service-duration" /></div>
              <div className="space-y-1.5"><Label>Fee (₹)</Label><Input type="number" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="flex items-center gap-3"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="service-active" /><Label>Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="service-submit">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
